import { ChartHistoryPropertyList, getChartHistoryPropertyWithParams } from '@/api/device-thingmodel';
import { useRequest } from 'ahooks';
import { Button, DatePicker, Form, Pagination, Select, Spin } from 'antd';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import React, { useEffect, useRef, useState } from 'react';

export interface MatrixChartProps {
  height?: number;
  deviceId: string;
}

const { RangePicker } = DatePicker;
const { useForm } = Form;

const MatrixChart: React.FC<MatrixChartProps> = ({ height = 600, deviceId }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const echartsRef = useRef<echarts.ECharts | null>(null);
  const [form] = useForm();
  const [matrixData, setMatrixData] = useState<ChartHistoryPropertyList[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 16, total: 0 });

  const { loading, run: fetchData } = useRequest(
    (params?: { page?: number; pageSize?: number }) => {
      if (!deviceId) {
        return Promise.resolve({ success: false, result: { list: [], total: 0, page: 1, pageSize: 16 } } as any);
      }
      const [start, end] = form.getFieldValue('range') || [dayjs().add(-10, 'minutes'), dayjs()];
      const aggregateTime = form.getFieldValue('aggregateTime');
      const aggregateMethod = form.getFieldValue('aggregateMethod');
      return getChartHistoryPropertyWithParams(deviceId, {
        start: start?.valueOf()!,
        end: end?.valueOf()!,
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 16,
        aggregateTime: aggregateTime && aggregateTime !== 'none' ? aggregateTime : undefined,
        aggregateMethod: aggregateTime && aggregateTime !== 'none' ? aggregateMethod : undefined,
      });
    },
    {
      manual: true,
      onSuccess: (res) => {
        if (res.success && res.result) {
          setMatrixData(res.result.list || []);
          setPagination({
            current: res.result.page || 1,
            pageSize: res.result.pageSize || 16,
            total: res.result.total || 0,
          });
        }
      },
    },
  );

  useEffect(() => {
    if (deviceId) {
      fetchData({ page: 1, pageSize: 16 });
    }
  }, [deviceId, fetchData]);

  useEffect(() => {
    if (!chartRef.current || !matrixData || matrixData.length === 0) return;

    if (!echartsRef.current) {
      echartsRef.current = echarts.init(chartRef.current);
    }

    const chart = echartsRef.current;

    // 计算矩阵尺寸：4*4
    const cols = 4;
    const rows = Math.ceil(matrixData.length / cols);

    // X轴数据：列索引（0, 1, 2, 3）
    const xAxisData = Array.from({ length: cols }, (_, i) => `Col${i}`);

    // Y轴数据：行索引（0, 1, 2, 3...）
    const yAxisData = Array.from({ length: rows }, (_, i) => ({ value: `Row${i}` }));

    // 属性索引映射：根据行列索引计算在 matrixData 中的位置
    const getPropertyIndex = (xidx: number, yidx: number) => {
      return yidx * cols + xidx;
    };

    const _matrixDimensionData = {
      x: xAxisData,
      y: yAxisData,
    };

    const option: any = {
      matrix: {
        x: {
          data: _matrixDimensionData.x,
          levelSize: 0,
          label: {
            show: false,
          },
        },
        y: {
          data: _matrixDimensionData.y,
          levelSize: 0,
          label: {
            show: false,
          },
        },
        corner: {
          data: [],
        },
        body: {
          data: [],
        },
        top: 30,
        bottom: 80,
        width: '90%',
        left: 'center',
      },
      tooltip: {
        trigger: 'axis',
      },
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 'all' as any,
          left: '10%',
          right: '10%',
          bottom: 30,
          height: 30,
          throttle: 120,
        },
        {
          type: 'inside',
          xAxisIndex: 'all' as any,
          throttle: 120,
        },
      ],
      grid: [],
      xAxis: [],
      yAxis: [],
      series: [],
    };

    // ------ Helpers Start ------
    function makeId(xidx: number, yidx: number) {
      return `${xidx}|${yidx}`;
    }

    function eachMatrixCell(cb: (xval: string, yval: string, xidx: number, yidx: number) => void) {
      _matrixDimensionData.y.forEach((yvalItem, yidx) => {
        const yval = yvalItem.value;
        _matrixDimensionData.x.forEach((xval, xidx) => {
          cb(xval, yval, xidx, yidx);
        });
      });
    }

    function generateSeriesData(reportData: Array<{ time: number; value: number }> | null) {
      if (!reportData || reportData.length === 0) {
        return [];
      }
      return reportData.map((item) => {
        const date = dayjs(item.time);
        const formattedTime = date.format('YYYY-MM-DD HH:mm:ss');
        return [formattedTime, item.value];
      });
    }

    eachMatrixCell((xval, yval, xidx, yidx) => {
      const id = makeId(xidx, yidx);
      const propertyIndex = getPropertyIndex(xidx, yidx);
      const listItem = matrixData[propertyIndex];

      // 如果该位置没有对应的属性，跳过
      if (!listItem) {
        return;
      }

      const seriesData = generateSeriesData(listItem?.report_data || null);

      option.grid.push({
        id,
        coordinateSystem: 'matrix',
        coord: [xval, yval],
        top: 25,
        bottom: 10,
        left: 'center',
        width: '90%',
        containLabel: true,
      });
      option.xAxis.push({
        type: 'category',
        id,
        gridId: id,
        scale: true,
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
      });
      option.yAxis.push({
        id,
        gridId: id,
        interval: Number.MAX_SAFE_INTEGER,
        scale: true,
        axisLabel: {
          show: false,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      });
      option.series.push({
        xAxisId: id,
        yAxisId: id,
        type: 'line',
        symbol: 'none',
        lineStyle: {
          lineWidth: 1,
        },
        data: seriesData,
      });
    });

    chart.setOption(option);

    // 在图表渲染后，使用 graphic 组件为每个图表添加标题
    // 需要延迟执行以确保图表已经渲染完成
    setTimeout(() => {
      const graphics: any[] = [];
      const chartWidth = chart.getWidth();
      const chartHeight = chart.getHeight();

      // matrix 配置参数（与 option.matrix 保持一致）
      const matrixTop = option.matrix.top || 30;
      const matrixBottom = option.matrix.bottom || 80;
      const matrixLeftPercent = 0.05; // (100% - 90%) / 2 = 5%
      const matrixWidthPercent = 0.9;
      const matrixLeft = chartWidth * matrixLeftPercent;
      const matrixWidth = chartWidth * matrixWidthPercent;

      // 计算每个单元格的尺寸
      const cellWidth = matrixWidth / cols;
      const availableHeight = chartHeight - matrixTop - matrixBottom;
      const cellHeight = availableHeight / rows;

      eachMatrixCell((xval, yval, xidx, yidx) => {
        const propertyIndex = getPropertyIndex(xidx, yidx);
        const listItem = matrixData[propertyIndex];

        if (!listItem) {
          return;
        }

        // 计算每个单元格的左边界和中心X位置
        const cellLeft = matrixLeft + xidx * cellWidth;
        const centerX = cellLeft + cellWidth / 2;
        // 计算每个单元格顶部的位置（grid top是25，标题在grid上方）
        const topY = matrixTop + yidx * cellHeight + 6;
        // 计算每个单元格的中心Y位置（用于显示 No Data）
        const centerY = matrixTop + yidx * cellHeight + cellHeight / 2;

        // 使用 Canvas 测量文本实际宽度（更精确）
        // 创建一个临时的 canvas 来测量文本宽度
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.font = '12px sans-serif';
          const textWidth = ctx.measureText(listItem.name).width;
          // 计算文本左边界，使文本在单元格内居中
          const textLeft = centerX - textWidth / 2;

          // 添加标题 - 使用实际测量的文本宽度来计算居中位置
          graphics.push({
            type: 'text',
            id: `title_${xidx}_${yidx}`,
            left: textLeft,
            top: topY,
            style: {
              text: listItem.name,
              fontSize: 12,
              fill: '#333',
              textAlign: 'left', // 使用 left 对齐，通过计算 left 位置来实现居中
              textVerticalAlign: 'middle',
            },
            z: 100,
          });
        } else {
          // 如果无法创建 canvas，回退到使用 centerX 和 textAlign: 'center'
          graphics.push({
            type: 'text',
            id: `title_${xidx}_${yidx}`,
            left: centerX,
            top: topY,
            style: {
              text: listItem.name,
              fontSize: 12,
              fill: '#333',
              textAlign: 'center',
              textVerticalAlign: 'middle',
            },
            z: 100,
          });
        }

        // 如果没有数据，显示 No Data
        if (!listItem.report_data || listItem.report_data.length === 0) {
          graphics.push({
            type: 'text',
            id: `noData_${xidx}_${yidx}`,
            left: centerX - 20,
            top: centerY,
            style: {
              text: 'No Data',
              fontSize: 14,
              fill: '#999',
              textAlign: 'center',
              textVerticalAlign: 'middle',
            },
            z: 100,
          });
        }
      });

      if (graphics.length > 0) {
        chart.setOption(
          {
            graphic: graphics,
          },
          false,
        );
      }
    }, 100);

    return () => {
      if (echartsRef.current) {
        echartsRef.current.dispose();
        echartsRef.current = null;
      }
    };
  }, [height, matrixData]);

  const handleSearch = () => {
    fetchData({ page: 1, pageSize: pagination.pageSize });
  };

  const handleReset = () => {
    form.resetFields();
    setPagination({ current: 1, pageSize: 16, total: 0 });
    fetchData({ page: 1, pageSize: 16 });
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    const newPageSize = pageSize ?? pagination.pageSize;
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: newPageSize,
    }));
    fetchData({ page, pageSize: newPageSize });
  };

  if (loading && !matrixData.length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: `${height}px` }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <div className="mb10" style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
        <Form layout="inline" form={form} style={{ flexWrap: 'nowrap' }}>
          <Form.Item name="range" initialValue={[dayjs().add(-10, 'minutes'), dayjs()]}>
            <RangePicker
              format="YYYY-MM-DD HH:mm:ss"
              placeholder={['开始日期', '结束日期']}
              style={{ width: 320 }}
              showTime={{
                hideDisabledOptions: true,
                defaultValue: [dayjs(dayjs().format('YYYY-MM-DD')), dayjs(dayjs().format('YYYY-MM-DD'))],
              }}
              presets={[
                { label: '最近10分钟', value: [dayjs().add(-10, 'minutes'), dayjs()] },
                { label: '最近20分钟', value: [dayjs().add(-20, 'minutes'), dayjs()] },
                { label: '最近30分钟', value: [dayjs().add(-30, 'minutes'), dayjs()] },
                { label: '最近1小时', value: [dayjs().add(-1, 'hours'), dayjs()] },
                { label: '最近3小时', value: [dayjs().add(-3, 'hours'), dayjs()] },
                { label: '最近5小时', value: [dayjs().add(-5, 'hours'), dayjs()] },
                { label: '最近7小时', value: [dayjs().add(-7, 'hours'), dayjs()] },
                { label: '最近12小时', value: [dayjs().add(-12, 'hours'), dayjs()] },
                { label: '最近24小时', value: [dayjs().add(-24, 'hours'), dayjs()] },
              ]}
            />
          </Form.Item>
          <Form.Item name="aggregateTime" initialValue="none" label="聚合时间">
            <Select
              style={{ width: 90 }}
              onChange={(value) => {
                if (value === 'none') {
                  form.setFieldValue('aggregateMethod', undefined);
                } else if (!form.getFieldValue('aggregateMethod')) {
                  form.setFieldValue('aggregateMethod', 'avg');
                }
              }}
            >
              <Select.Option value="none">不聚合</Select.Option>
              <Select.Option value="30s">30秒</Select.Option>
              <Select.Option value="1m">1分钟</Select.Option>
              <Select.Option value="2m">2分钟</Select.Option>
              <Select.Option value="5m">5分钟</Select.Option>
              <Select.Option value="10m">10分钟</Select.Option>
              <Select.Option value="30m">30分钟</Select.Option>
              <Select.Option value="1h">1小时</Select.Option>
              <Select.Option value="3h">3小时</Select.Option>
              <Select.Option value="6h">6小时</Select.Option>
              <Select.Option value="12h">12小时</Select.Option>
              <Select.Option value="1d">1天</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.aggregateTime !== currentValues.aggregateTime}>
            {() => {
              const aggregateTime = form.getFieldValue('aggregateTime');
              return aggregateTime && aggregateTime !== 'none' ? (
                <Form.Item name="aggregateMethod" initialValue="avg" label="聚合方法">
                  <Select style={{ width: 80 }}>
                    <Select.Option value="avg">平均数</Select.Option>
                    <Select.Option value="max">最大值</Select.Option>
                    <Select.Option value="min">最小值</Select.Option>
                    <Select.Option value="sum">求和</Select.Option>
                  </Select>
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
          <Form.Item style={{ marginRight: 0, whiteSpace: 'nowrap' }}>
            <Button type="primary" onClick={handleSearch}>
              查询
            </Button>
            <Button className="ml10" onClick={handleReset}>
              重置
            </Button>
          </Form.Item>
        </Form>
      </div>

      {loading && matrixData.length > 0 ? (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spin />
          </div>
          <div ref={chartRef} style={{ width: '100%', height: `${height}px`, minWidth: 0 }} />
        </div>
      ) : (
        <div ref={chartRef} style={{ width: '100%', height: `${height}px`, minWidth: 0 }} />
      )}

      {!matrixData || matrixData.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: `${height}px`, color: '#999' }}>暂无数据</div>
      ) : null}

      {pagination.total > 0 && (
        <div className="mt20" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total) => `共 ${total} 条`}
            showPrevNextJumpers
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
            pageSizeOptions={['16', '32', '48', '64']}
          />
        </div>
      )}
    </div>
  );
};

export default MatrixChart;
